import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { ApiError } from '@/services/api/client';

/**
 * Created inside the component, not at module scope.
 *
 * A module-level client is shared by every test in a file and by every render
 * in development, so state leaks between them. useState gives one per mount.
 */
export function QueryProvider({ children }: { children: ReactNode }) {
  const [client] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            /*
             * Refetching on every window focus is the default and it is wrong
             * for this app: a lab technician alt-tabbing between the portal and
             * NuCAD would refire every query each time.
             */
            refetchOnWindowFocus: false,
            retry: (failureCount, error) => {
              // 4xx means the request was wrong; repeating it changes nothing.
              if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
                return false;
              }
              return failureCount < 2;
            },
          },
          mutations: {
            // Never automatic: a retried POST can create two of something.
            retry: false,
          },
        },
      }),
  );

  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}
